import { useState, type ReactNode } from 'react';
import { logoSrc } from '@/lib/logo';

/**
 * The company's logo as an `<img>` (M2-19), shared by the header, the
 * letterhead and the settings preview so the three cannot drift.
 *
 * - `referrerPolicy="no-referrer"` on every one: on the customer's page the
 *   page's own URL carries the link's token (M2-07), and no image request may
 *   forward it. The SPA's origin-only default already strips the path; this
 *   does not rely on it.
 * - **A logo that fails to load falls back** to `fallback` (nothing, by
 *   default) rather than a broken-image box. The failure is remembered per
 *   `src`, so a replacement (a new `?v=`) is tried afresh.
 * - `frameClassName` wraps the image in a box of known size, so the space is
 *   held before the bytes arrive and nothing below it moves when they do. A
 *   failed logo takes its frame with it.
 */
export function CompanyLogo({
  path,
  alt,
  className,
  testId,
  frameClassName,
  fallback = null,
}: {
  path: string | null | undefined;
  alt: string;
  className?: string;
  testId?: string;
  frameClassName?: string;
  fallback?: ReactNode;
}) {
  const src = logoSrc(path);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (!src || src === failedSrc) return <>{fallback}</>;
  const img = (
    <img
      src={src}
      alt={alt}
      referrerPolicy="no-referrer"
      className={className}
      data-testid={testId}
      onError={() => setFailedSrc(src)}
    />
  );
  return frameClassName ? <div className={frameClassName}>{img}</div> : img;
}
